import { useEffect, useRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';
import {
    DEFAULT_PAGE_COUNT,
    ratingColors,
    ratingText,
    BORDER_WIDTH,
    badge,
    wrap
} from './Constants';
import { prepareChartData } from './prepareChartData';
import { METRIC_KEYS, METRIC_LABELS } from './Constants';
import { renderDesignBands } from './designBandOverlay';

export default function useSunburstChart({ movie, includeScenes, metric, onSelect, animateAct1 }) {
  // export default function useSunburstChart({ movie, includeScenes, onSelect, animateAct1 }) {
  const ref = useRef();
  const clickTracker = useRef({ id: null, time: 0, count: 0 });
  const currentLevelRef = useRef(null); // Track current zoom level
  const selectedNodeRef = useRef(null); // Track the currently selected node
  const chartDataRef = useRef(null); // Store the current chart data
const hasInitialized = useRef(false); // NEW: Track if we've done initial setup

  useEffect(() => {
    if (!movie) return;

    // const { nodes } = prepareChartData(movie, includeScenes);
    const { nodes } = prepareChartData(movie, includeScenes, metric);
    
    // Store the nodes for later reference
    chartDataRef.current = { nodes };

    const ids = [];
    const labels = [];
    const parents = [];
    const values = [];
    const colors = [];
    const borders = [];
    const customdata = [];

    // Set default styling
    const DEFAULT_BORDER_COLOR = 'rgb(0, 0, 0)';
    const DEFAULT_BORDER_WIDTH = 2;
    const HIGHLIGHT_BORDER_COLOR = 'rgb(255, 255, 255)';
    const HIGHLIGHT_BORDER_WIDTH = 4;
    
    nodes.forEach(node => {
      ids.push(node.id);
      labels.push(node.formattedLabel || node.label);
      parents.push(node.parent);
      values.push(node.value);
      colors.push(node.color || '#cccccc');
      borders.push(DEFAULT_BORDER_COLOR);
      
      customdata.push(createCustomData(node, movie, metric));
    });

    const textSizes = [];

    nodes.forEach(node => {
      if (node.nodeType === 'movie' || node.nodeType === 'act') {
        textSizes.push(25); // Big text for movie & act
      } else if (node.nodeType === 'sequence') {
        textSizes.push(18); // Medium for sequences
      } else if (node.nodeType === 'scene') {
        textSizes.push(12); // Normal for scenes
      } else {
        textSizes.push(10); // Gaps or unknowns
      }
    });
        
    // Create a map of node IDs to determine which nodes have children
    const nodeChildrenMap = {};
    nodes.forEach(node => {
      nodeChildrenMap[node.id] = [];
    });
    nodes.forEach(node => {
      if (node.parent && nodeChildrenMap[node.parent]) {
        nodeChildrenMap[node.parent].push(node.id);
      }
    });
    
    // Function to check if a node is a leaf (has no children)
    function isLeafNode(nodeId) {
      return !nodeChildrenMap[nodeId] || nodeChildrenMap[nodeId].length === 0;
    }

    const data = [{
      type: 'sunburst',
      ids,
      labels,
      parents,
      values,
      textinfo: 'label',
      hovertemplate: '%{customdata}<extra></extra>',
      customdata,
      textfont: {
        size: textSizes,
        color: '#000'
      },
      marker: {
        colors,
        line: {
          color: borders,
          width: Array(ids.length).fill(DEFAULT_BORDER_WIDTH)
        }
      },
      branchvalues: 'total',
      sort: false,
      maxdepth: includeScenes ? 4 : 3,
      rotation: 90
    }];

    const layout = {
      margin: { l: 0, r: 0, t: 0, b: 0 },
      height: ref.current?.clientHeight || 600,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      dragmode: false,
      showlegend: false,
      clickmode: '',
      hoverlabel: {
              bgcolor: '#fdfdfb',          // <- pure white
              bordercolor: '#cbd5e1',      // soft grey border
              font: { color: '#0f172a' },  // near-black text
            },      transition: {
        duration: 500,
        easing: 'cubic-in-out'
      }
    };

    const config = { 
      displayModeBar: false, 
      responsive: true
    };

    Plotly.react(ref.current, data, layout, config);

    // ── Dual-color overlay: execution = Plotly's wedge fill (core); design =
    // a thin frame the overlay paints around each scene. The overlay matches
    // slices to nodes by id (via slice.__data__), so DOM slice order doesn't
    // matter. It is pointer-events:none, so clicks (incl. double-click zoom)
    // pass through to Plotly's wedges untouched.
    //
    // The design frame is METRIC-GATED: it encodes the design score, which is
    // only coherent when the fill encodes execution — i.e. metric === 'overall'.
    // On any other metric (pacing, emotion, …) the fill shows that metric, so
    // a design frame would pair two unrelated encodings; we null the scores
    // instead of skipping renderDesignBands entirely because the overlay also
    // draws the level-separator rings and the selection focus ring, which must
    // persist on every metric.
    const showDesignFrames = metric === 'overall';
    const sceneOverlayNodes = nodes.map(n => ({
      nodeType: n.nodeType,
      id: n.id,
      design_score: showDesignFrames ? (n.detailedContent?.design ?? null) : null,
    }));
    renderDesignBands(ref.current, sceneOverlayNodes, selectedNodeRef.current);
    // plotly_afterplot fires after every redraw (zoom, resize, animation
    // completion), so the overlay always re-aligns and keeps the focus ring.
    const afterPlotHandler = () => renderDesignBands(ref.current, sceneOverlayNodes, selectedNodeRef.current);
    ref.current.on('plotly_afterplot', afterPlotHandler);

    // Selection is now drawn by the overlay as a focus ring (blue + white halo)
    // around the selected wedge. This is reliable (matched by id, redrawn on
    // every Plotly redraw) and — unlike the old white marker.line restyle — it
    // doesn't fight Plotly's per-wedge borders, isn't hidden under the design
    // frame, and avoids a Plotly.restyle (a full redraw) on every click.
    function highlightNode(nodeId) {
      if (!nodeId) return;
      selectedNodeRef.current = nodeId;
      renderDesignBands(ref.current, sceneOverlayNodes, nodeId);
    }

    // Function to remove highlight and restore normal appearance
    function unhighlightNode() {
      renderDesignBands(ref.current, sceneOverlayNodes, null);
      selectedNodeRef.current = null;
    }

    // Function to zoom to a specific level
    function zoomToLevel(levelId) {
      console.log('Attempting to zoom to level:', levelId);
      
      try {
        // Remember the current selection
        const prevSelection = selectedNodeRef.current;
        
        // Create a new data object with the level property
        const newData = [{
          ...data[0],
          level: levelId
        }];
        
        // Update the chart with the new data
        Plotly.react(ref.current, newData, layout, config);
        
        // Update our tracking of the current level
        currentLevelRef.current = levelId;
        console.log('Current level set to:', currentLevelRef.current);
        
        // Reapply highlight after zooming if needed
        if (prevSelection) {
          setTimeout(() => highlightNode(prevSelection), 600);
        }
      } catch (error) {
        console.error('Error zooming to level:', error);
      }
    }

    // Function to unzoom one level up
    function unzoomOneLevel() {
      const currentId = currentLevelRef.current;
      if (!currentId) {
        console.log('Already at root level');
        return;
      }
      
      console.log('Current level:', currentId);
      
      // Find the parent of the current level
      const currentNode = nodes.find(n => n.id === currentId);
      if (!currentNode || !currentNode.parent) {
        console.log('No parent found or already at root');
        // If no parent or already at root, go to root level
        zoomToLevel('');
        return;
      }
      
      // Get the parent ID and zoom to it
      const parentId = currentNode.parent;
      console.log('Unzooming to parent:', parentId);
      zoomToLevel(parentId);
    }

    // Define the handler for sunburst clicks
    const sunburstClickHandler = ev => {
      console.log('Sunburst click event:', ev);
      
      // Extract ID and nextLevel from event data
      const id = ev?.points?.[0]?.id;
      const nextLevel = ev?.nextLevel;
      
      console.log('Clicked ID:', id, 'Next Level:', nextLevel);
      
      if (!id) return false;
      
      // Find the node object and show panel
      const nodeObj = nodes.find(n => n.id === id);
      if (nodeObj?.detailedContent) {
        // Always show the side panel on click
        onSelect(nodeObj.detailedContent, metric);
        
        // Highlight the selected node
        highlightNode(id);
      }
      
      // Check if this is a leaf node - don't zoom on leaves
      const isLeaf = isLeafNode(id);
      console.log(`Node ${id} is ${isLeaf ? 'a leaf' : 'not a leaf'}`);
      
      // Check for double-click using time-based detection
      const now = Date.now();
      const lastClick = clickTracker.current;
      
      if (lastClick.id === id && now - lastClick.time < 300) {
        console.log('Double click detected on:', id);
        
        // If it's a leaf node, don't do any zooming
        if (isLeaf) {
          console.log('Leaf node clicked, not zooming');
          // Still show the panel but don't zoom
        } else {
          // Check if we clicked on the center while already zoomed in
          const isCenter = id === currentLevelRef.current;
          
          if (isCenter && currentLevelRef.current) {
            // If clicked on center and already zoomed in, unzoom one level
            console.log('Center clicked while zoomed in, unzooming...');
            unzoomOneLevel();
          } else {
            // Otherwise, zoom to the clicked level
            if (nextLevel !== undefined) {
              console.log('Zooming to next level:', nextLevel);
              zoomToLevel(nextLevel);
            } else {
              console.log('No next level defined, zooming to ID:', id);
              zoomToLevel(id);
            }
          }
        }
        
        // Reset click tracker
        clickTracker.current = { id: null, time: 0, count: 0 };
      } else {
        // Update the click tracker for first click
        clickTracker.current = { id, time: now, count: 1 };
      }
      
      // Return false to prevent default zoom
      return false;
    };

    // Add the handler
    ref.current.on('plotly_sunburstclick', sunburstClickHandler);

    // Handle window resize events to reapply highlighting
    const handleResize = () => {
      // Re-align the design bands with Plotly's resized wedges.
      setTimeout(() => renderDesignBands(ref.current, sceneOverlayNodes), 100);
      // If there's a selected node, reapply the highlight after resize
      if (selectedNodeRef.current) {
        setTimeout(() => highlightNode(selectedNodeRef.current), 100);
      }
    };

    window.addEventListener('resize', handleResize);

    if (animateAct1) {
      setTimeout(() => pulsePaths(ref.current, nodes, 'act-1'), 400);
    }

if (!hasInitialized.current) {
      const movieNode = nodes.find(n => n.id === "Movie");
      if (movieNode?.detailedContent) {
        onSelect(movieNode.detailedContent, metric);
        // Highlight the movie node
        setTimeout(() => highlightNode("Movie"), 300);
      }
      hasInitialized.current = true; // Mark as initialized
    }

    return () => {
      if (ref.current?.removeAllListeners) {
        ref.current.removeAllListeners('plotly_sunburstclick');
      }
      // Remove ONLY our afterplot handler — NOT removeAllListeners('plotly_afterplot'),
      // which would also tear out Plotly's own internal afterplot listeners (under
      // React StrictMode's mount→cleanup→remount this breaks zoom/transition).
      if (ref.current?.removeListener) {
        ref.current.removeListener('plotly_afterplot', afterPlotHandler);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [movie, includeScenes, onSelect, animateAct1, metric]);

  return ref;
}

function createCustomData(node, movie, metric) {
  const info = node.detailedContent;
  if (!info) return "";

  let hoverText = `<b>${info.title || node.label}</b>`;

  // ── Scenes: a quick writer's take — scene type, one-line overview, and the
  // two scores in plain language (no chart-mechanic "fill/band" jargon). ──
  if (node.nodeType === 'scene') {
    const fmt = v => Number(v).toFixed(1);
    const wrap = (str, lim = 44) => {
      if (!str) return '';
      const words = String(str).split(' ');
      let out = '', line = '';
      words.forEach(w => { if ((line + w).length > lim) { out += line.trim() + '<br>'; line = ''; } line += w + ' '; });
      return out + line.trim();
    };
    const de = info.design_execution || null;
    if (de?.scene_type) hoverText += `  <span style="opacity:.7">(${de.scene_type})</span>`;
    if (de?.reason) hoverText += `<br><em>${wrap(de.reason, 44)}</em>`;
    const parts = [];
    if (info.execution != null) parts.push(`<b>Execution</b> ${fmt(info.execution)}/10`);
    if (info.design != null) parts.push(`<b>Design</b> ${fmt(info.design)}/10`);
    if (parts.length) hoverText += `<br>${parts.join('  ·  ')}`;
    else if (info.design == null) hoverText += `<br><span style="opacity:.6">design not analyzed yet</span>`;
    if (info.page_count) hoverText += `<br><span style="opacity:.7">Pages: ${info.page_count}</span>`;
    return hoverText;
  }

  // Figure out which score/explanation to show based on metric
  const key = METRIC_KEYS[metric]?.[node.nodeType];
  const scoreObj = info.scores?.[key];
  const scoreVal = metric === 'overall'
        ? (info.execution ?? info.analysis?.overallScore)
        : scoreObj?.score;

  // (Scenes returned early above.) Acts/sequences/movie: the generic metric line.
  if (scoreVal !== undefined) {
    hoverText += `<br><b>${METRIC_LABELS[metric]}:</b> ${Number(scoreVal).toFixed(1)}/10`;
  }

  const hardWrap = (str, limit = 40) => {
    if (!str) return '';
    const words = str.split(' ');
    let out = '', line = '';
    words.forEach(w => {
      if ((line + w).length > limit) {
        out += `${line.trim()}<br>`;
        line = '';
      }
      line += w + ' ';
    });
    return out + line.trim();
  };

  const explanation = metric === 'overall'
        ? (info.execution_summary ?? info.analysis?.hoverSummary)
        : scoreObj?.explanation;

  if (explanation) {
    hoverText += `<br><em>${hardWrap(explanation, 40)}</em>`;
  }

  // Add improvement suggestions if they exist for the selected metric
  if (metric !== 'overall' && scoreObj?.improvementSuggestions?.length > 0) {
    hoverText += `<br><br><b>Suggestions:</b>`;
    scoreObj.improvementSuggestions.forEach((suggestion, index) => {
      // Limit to first 2 suggestions to keep hover manageable
      if (index < 2) {
        hoverText += `<br>• ${hardWrap(suggestion, 35)}`;
      }
    });
    
    // If there are more than 2 suggestions, indicate that
    if (scoreObj.improvementSuggestions.length > 2) {
      hoverText += `<br>• (+${scoreObj.improvementSuggestions.length - 2} more...)`;
    }
  }

  if (node.nodeType === 'scene' && info.page_count) {
    hoverText += `<br>Pages: ${info.page_count}`;
  }
  
  return hoverText;
}

function label(r) {
  return ["Pass", "Consider", "Recommend", "Highly Recommend"][r] ?? `Rating ${r}`;
}

function pulsePaths(el, nodes, rootId) {
  if (!el) return; // <-- ADD THIS LINE
  const ids = nodes.map(n => n.id);
  const paths = el.querySelectorAll('.slice path');
  paths.forEach((p, i) => {
    if (ids[i] && ids[i].startsWith(rootId)) p.classList.add('pulse');
  });
  setTimeout(() => paths.forEach(p => p.classList.remove('pulse')), 3500);
}